/**
 * CustomFontFamilyCommand class for CKEditor
 * 
 * This command is responsible for applying and managing custom font family styling in CKEditor. It extends the 
 * `CustomFontCommand` class to handle the font family attribute. When executed, it updates the editor's selection 
 * with the selected font family. It also ensures compatibility with the editor's model schema and selection 
 * handling. This command is used in conjunction with the `CustomFontFamilyUI` plugin to provide an interface 
 * for font family selection.
 * 
 * ## Features
 * - **Font Family Styling**:
 *   - Allows the application of a custom font family to selected text.
 *   - Works in both collapsed and non-collapsed selections.
 * - **Command Integration**:
 *   - Tied to the `CustomFontFamily` key defined in the CKPlugins constants.
 *   - Uses a shared command structure from the `CustomFontCommand` class.
 * - **Model Schema Compatibility**:
 *   - Ensures that the font family attribute is supported within the CKEditor schema for text elements.
 *   - Supports the application and removal of font family styling.
 * 
 * ## Usage
 * This command is typically used in the context of a CKEditor plugin like `CustomFontFamilyUI` that provides
 * an interface for selecting and applying font families. The command itself can be executed by the plugin to
 * apply the chosen font family to the editor content.
 * 
 * Example of usage:
 * ```javascript
 * editor.execute(CKPlugins.CustomFontFamily, { value: 'Arial' });
 * ```
 * 
 * ## Implementation Details
 * - The `CustomFontFamilyCommand` inherits from `CustomFontCommand`, which already provides the logic 
 *   for handling the font family attribute in the editor.
 * - The constructor accepts an editor instance and passes it along with the font family constant to the 
 *   parent class.
 * - The `CustomFontFamilyCommand` will automatically update the font family attribute in the selection 
 *   when executed.
 * 
 * ## References
 * - CKEditor Command API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_command-Command.html
 * - CKEditor Plugin API:
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * 
 * @module CustomFontFamilyCommand
 * @exports CustomFontFamilyCommand
 */


import { CKPlugins } from '../../constants/constants';
import { CustomFontCommand } from '../../font-color/adapters/fontcolorCommand';

export class CustomFontFamilyCommand extends CustomFontCommand {
    constructor(editor) {
        super(editor, CKPlugins.CustomFontFamily);
    }
}
